// Business-date helpers — the frontend mirror of backend/timeutil.py. Read
// that file's docstring first; the reasoning is identical and the two must
// agree, because dates written here are read back by the billing code there.
//
// The 3PL operates in Australia. `new Date().toISOString().slice(0, 10)`
// yields the UTC calendar date, which is YESTERDAY in Sydney between midnight
// and 10am (11am once AEDT starts). That is what stamped a manual warehouse
// entry made on the morning of 28/09 as 27/09, while the Slack ingest — which
// converts to Sydney at backend/routers/warehouse.py:779 — correctly recorded
// 28/09 for the same day's work.
//
// Browser-local time would usually be right for Australian staff, but only by
// coincidence: it breaks silently for anyone working from another timezone and
// it is not what the backend uses. Resolving Australia/Sydney explicitly makes
// the manual and Slack paths agree by construction rather than by luck.
//
// USE these for any date STORED as a business fact or used as a billing-period
// default — activity_date, started_on/ended_on, ship_date, label_created_at,
// received_date, and report range defaults.
//
// DO NOT use them for audit timestamps (created_at, synced_at, updated_at).
// Those stay UTC, exactly as timeutil.py says.

// A NAMED zone, not a fixed offset, so boundaries stay correct across the
// AEST/AEDT transitions (AEDT begins the first Sunday in October).
export const BUSINESS_TZ = 'Australia/Sydney'

// Built once — constructing an Intl.DateTimeFormat is expensive and these are
// called from render paths. 'en-CA' is the locale trick that yields an
// ISO-shaped YYYY-MM-DD, which is what <input type="date"> and the API expect.
const _isoDateFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TZ,
  year:  'numeric',
  month: '2-digit',
  day:   '2-digit',
})

/** Today's calendar date in the business timezone, as 'YYYY-MM-DD'. */
export function businessToday() {
  return _isoDateFmt.format(new Date())
}

/** First day of the current business month, as 'YYYY-MM-DD'. */
export function businessMonthStart() {
  return `${businessToday().slice(0, 7)}-01`
}

/**
 * Shift a 'YYYY-MM-DD' string by n days (negative to go back).
 *
 * Pure calendar arithmetic, done entirely in UTC so it can never pick up a
 * timezone shift. The naive version — new Date(iso + 'T00:00:00') to parse at
 * LOCAL midnight, mutate, then .toISOString() to serialise through UTC —
 * silently cancels its own offset in any UTC+ zone, which is how
 * StorageTab's "day after the entry started" guard ended up returning the
 * start date itself.
 */
export function addDays(iso, n) {
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y || !m || !d) return iso
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + n)
  return dt.toISOString().slice(0, 10)
}
